import React, { useState, useRef, useEffect } from "react";
import { getExamQuestionStats } from "../utils/examScoring";

function FinishScreen({
  points,
  maxPossiblePoints,
  highscore,
  candidateName,
  dispatch,
  numQuestions,
  answers = [],
  questions = [],
  flaggedQuestions = [],
  incorrectQuestions = null,
  examMode,
  selectedBankName,
  onReviewExam,
  onRetakeAll,
  onRetakeFlagged,
  onRetakeIncorrect,
  onClose,
  backButtonLabel,
}) {
  const [isRetakeMenuOpen, setIsRetakeMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsRetakeMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const percentage =
    maxPossiblePoints > 0 ? (points / maxPossiblePoints) * 100 : 0;
  const ciscoScaleScore = Math.round((percentage / 100) * 1000); // 0 - 1000 scale
  const isPassed = percentage >= 82.5;

  const stats = (questions && questions.length > 0)
    ? getExamQuestionStats(questions, answers)
    : {
        total: numQuestions || 0,
        answered: Array.isArray(answers) ? answers.filter((a) => a !== null && a !== undefined).length : 0,
        correct: Math.max(0, (numQuestions || 0) - (Array.isArray(incorrectQuestions) ? incorrectQuestions.length : 0)),
        incorrect: Array.isArray(incorrectQuestions) ? incorrectQuestions.length : 0,
        unanswered: 0,
        nonCorrectIndices: Array.isArray(incorrectQuestions) ? incorrectQuestions : [],
      };

  const totalCount = numQuestions || stats.total || (questions ? questions.length : 0);
  const correctCount = stats.correct;
  const incorrectCount = stats.incorrect; // answered, but wrong
  const unansweredCount = stats.unanswered; // skipped / missed
  const nonCorrectCount = stats.nonCorrectIndices.length; // total non-correct
  const flaggedCount = flaggedQuestions ? flaggedQuestions.length : 0;

  return (
    <div className="finish-screen-container">
      <div className={`cisco-score-report-card ${isPassed ? "card-pass" : "card-fail"}`}>
        <div className="report-header">
          <div className="report-title-area">
            <span className="report-sub">Final Score: ExSim-Max for Cisco 200-301 CCNA</span>
            <h2 className="report-main-title">{selectedBankName || "CCNA Examination"}</h2>
          </div>
          <div className="report-header-right-group">
            <div className={`report-status-badge ${isPassed ? "badge-pass" : "badge-fail"}`}>
              {isPassed ? "PASS ✓" : "FAIL ✕"}
            </div>
            {onClose && (
              <button
                type="button"
                className="btn-report-close-modal"
                onClick={onClose}
                aria-label="Close"
                title="Close Score Report"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {candidateName && (
          <div className="report-candidate-bar">
            Candidate: <strong>{candidateName}</strong>
            <span className="candidate-bar-sep">•</span>
            Exam Mode: <strong>{examMode === "study" ? "Study Mode" : "Simulation Mode"}</strong>
          </div>
        )}

        <div className="report-metrics-grid">
          <div className="metric-box">
            <span className="metric-label">Your Scaled Score</span>
            <span className={`metric-value ${isPassed ? "highlight text-green" : "text-danger"}`}>
              {ciscoScaleScore}
            </span>
            <span className="metric-meta">out of 1000 ({points} Points)</span>
          </div>

          <div className="metric-box">
            <span className="metric-label">Passing Score</span>
            <span className="metric-value">825</span>
            <span className="metric-meta">Required (82.5%)</span>
          </div>

          <div className="metric-box">
            <span className="metric-label">Accuracy Percentage</span>
            <span className={`metric-value ${isPassed ? "text-green" : "text-danger"}`}>
              {percentage.toFixed(1)}%
            </span>
            <span className="metric-meta">{points} / {maxPossiblePoints} Points</span>
          </div>

          <div className="metric-box">
            <span className="metric-label">Questions Summary</span>
            <span className="metric-value">{correctCount} / {totalCount}</span>
            <span className="metric-meta">
              {correctCount} Correct, {incorrectCount} Incorrect{unansweredCount > 0 ? `, ${unansweredCount} Missed` : ""}{flaggedCount > 0 ? `, ${flaggedCount} Marked` : ""}
            </span>
          </div>
        </div>

        {/* BOSON EXSIM SCORE BAR WITH PASSING SCORE PIN */}
        <div className="boson-score-bar-section">
          <div className="boson-score-track-wrap">
            <div className="boson-score-track">
              <div
                className={`boson-score-fill ${isPassed ? "fill-pass" : "fill-fail"}`}
                style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
              ></div>
            </div>
            <div className="boson-passing-pin" style={{ left: "82.5%" }}>
              <div className="pin-triangle">▲</div>
              <span className="pin-label">Passing Score</span>
            </div>
          </div>
        </div>

        {/* ACTION BUTTONS & RETAKE DROPDOWN */}
        <div className="report-action-buttons">
          {/* RETAKE DROPDOWN */}
          <div className="retake-dropdown-container" ref={menuRef}>
            <button
              type="button"
              className="btn-report-retake-toggle"
              onClick={() => setIsRetakeMenuOpen((prev) => !prev)}
            >
              <span>↺ Retake Exam ▾</span>
            </button>

            {isRetakeMenuOpen && (
              <div className="retake-dropdown-menu">
                <button
                  type="button"
                  className="retake-dropdown-item"
                  onClick={() => {
                    setIsRetakeMenuOpen(false);
                    if (onReviewExam) {
                      onReviewExam();
                    } else {
                      dispatch({ type: "reviewExam", payload: 0 });
                    }
                  }}
                >
                  <span className="item-icon">↶</span>
                  <div className="item-text-wrap">
                    <strong>Revise Previous Answers</strong>
                    <span className="item-sub">Review completed questions & show explanations</span>
                  </div>
                </button>

                <button
                  type="button"
                  className="retake-dropdown-item"
                  onClick={() => {
                    setIsRetakeMenuOpen(false);
                    if (onRetakeAll) {
                      onRetakeAll();
                    } else {
                      dispatch({ type: "restart" });
                    }
                  }}
                >
                  <span className="item-icon">↺</span>
                  <div className="item-text-wrap">
                    <strong>Retake All Questions</strong>
                    <span className="item-sub">Start same exam from scratch ({numQuestions} Qs)</span>
                  </div>
                </button>

                <button
                  type="button"
                  className={`retake-dropdown-item ${flaggedCount === 0 ? "disabled" : ""}`}
                  disabled={flaggedCount === 0}
                  onClick={() => {
                    if (flaggedCount === 0) return;
                    setIsRetakeMenuOpen(false);
                    if (onRetakeFlagged) onRetakeFlagged();
                  }}
                >
                  <span className="item-icon">⚑</span>
                  <div className="item-text-wrap">
                    <strong>Retake Marked Questions</strong>
                    <span className="item-sub">{flaggedCount > 0 ? `${flaggedCount} marked for review` : "No marked questions"}</span>
                  </div>
                </button>

                <button
                  type="button"
                  className={`retake-dropdown-item ${nonCorrectCount === 0 ? "disabled" : ""}`}
                  disabled={nonCorrectCount === 0}
                  onClick={() => {
                    if (nonCorrectCount === 0) return;
                    setIsRetakeMenuOpen(false);
                    if (onRetakeIncorrect) onRetakeIncorrect();
                  }}
                >
                  <span className="item-icon">✕</span>
                  <div className="item-text-wrap">
                    <strong>
                      {unansweredCount > 0
                        ? `Retake Incorrect & Missed (${nonCorrectCount})`
                        : `Retake Incorrect Only (${incorrectCount})`}
                    </strong>
                    <span className="item-sub">
                      {nonCorrectCount > 0
                        ? unansweredCount > 0
                          ? `${incorrectCount} incorrect, ${unansweredCount} missed`
                          : `${incorrectCount} answered incorrectly`
                        : "All answers correct!"}
                    </span>
                  </div>
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn-report-review"
            onClick={() => {
              if (onReviewExam) {
                onReviewExam();
              } else if (dispatch) {
                dispatch({ type: "reviewExam", payload: 0 });
              }
            }}
          >
            🔍 Review All Questions
          </button>

          <button
            type="button"
            className="btn-report-restart"
            onClick={() => {
              if (onClose) {
                onClose();
              } else if (dispatch) {
                dispatch({ type: "restart" });
              }
            }}
          >
            {onClose ? (backButtonLabel || "← Back to History") : "⌂ Back to Exam Selection"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FinishScreen;
